<script setup>
import { ref, watch } from 'vue';
import { useElementSize } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Bubble from 'dashboard/components-next/message/bubbles/Bubble.vue';
import {
  MESSAGE_VARIANTS,
  ORIENTATION,
} from 'dashboard/components-next/message/constants';
import InternalChatComposer from './InternalChatComposer.vue';
import InternalChatThreadHistory from './InternalChatThreadHistory.vue';
import InternalChatMessageContent from './InternalChatMessageContent.vue';

const props = defineProps({
  thread: { type: Object, default: null },
  currentUserId: { type: Number, default: null },
  saveMessage: { type: Function, required: true },
  draft: { type: Object, default: null },
  isSending: { type: Boolean, default: false },
  error: { type: String, default: '' },
  members: { type: Object, default: () => ({ users: [], teams: [] }) },
  canMentionAll: { type: Boolean, default: false },
  canManageMessages: { type: Boolean, default: false },
  canEditOwnMessages: { type: Boolean, default: true },
  actionError: { type: String, default: '' },
  historyState: { type: Object, required: true },
  loadOlder: { type: Function, required: true },
  loadNewer: { type: Function, required: true },
  focusedMessageId: { type: Number, default: null },
  refresh: { type: Function, required: true },
});

const emit = defineEmits(['close', 'send', 'draftChange', 'typing', 'delete']);
const { t } = useI18n();
const composer = ref(null);
const history = ref(null);
const editorContainer = ref(null);
const { height: editorContainerHeight } = useElementSize(editorContainer);
const senderName = message =>
  message.sender?.display_name ||
  message.sender?.name ||
  t('INTERNAL_CHAT.UNKNOWN_SENDER');
const resetComposer = payload => composer.value?.reset(payload);
watch(
  () => props.thread?.parent?.id,
  () => resetComposer()
);
const focusMessage = id => history.value?.focusMessage(id);
defineExpose({ resetComposer, focusMessage });
</script>

<template>
  <aside
    v-if="thread"
    class="absolute inset-0 z-20 flex w-full shrink-0 flex-col border-s border-n-weak bg-n-surface-1 md:static md:w-80"
  >
    <header
      class="flex h-14 shrink-0 items-center justify-between border-b border-n-weak px-4"
    >
      <h2 class="m-0 text-sm font-semibold text-n-slate-12">
        {{ t('INTERNAL_CHAT.THREAD') }}
      </h2>
      <Button
        type="button"
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-x"
        :aria-label="t('INTERNAL_CHAT.CLOSE_THREAD')"
        @click="$emit('close')"
      />
    </header>
    <div ref="editorContainer" class="flex min-h-0 flex-1 flex-col">
      <article
        class="max-h-[35%] shrink-0 overflow-y-auto border-b border-n-weak p-4"
        data-testid="internal-chat-thread-parent"
      >
        <Bubble
          :variant="
            thread.parent.sender_id === currentUserId
              ? MESSAGE_VARIANTS.AGENT
              : MESSAGE_VARIANTS.USER
          "
          :orientation="
            thread.parent.sender_id === currentUserId
              ? ORIENTATION.RIGHT
              : ORIENTATION.LEFT
          "
          class="px-4 py-3"
        >
          <p class="mb-1 text-xs font-medium text-n-slate-10">
            {{ senderName(thread.parent) }}
          </p>
          <InternalChatMessageContent :message="thread.parent" />
        </Bubble>
      </article>
      <InternalChatThreadHistory
        ref="history"
        :thread="thread"
        :current-user-id="currentUserId"
        :save-message="saveMessage"
        :members="members"
        :can-mention-all="canMentionAll"
        :can-manage-messages="canManageMessages"
        :can-edit-own-messages="canEditOwnMessages"
        :action-error="actionError"
        :history-state="historyState"
        :load-older="loadOlder"
        :load-newer="loadNewer"
        :focused-message-id="focusedMessageId"
        :refresh="refresh"
        @delete="emit('delete', $event)"
      />
      <InternalChatComposer
        ref="composer"
        :container-height="editorContainerHeight"
        :disabled="isSending || historyState.isOpening"
        :error="error"
        :draft="draft"
        :members="members"
        :can-mention-all="canMentionAll"
        is-thread
        @send="emit('send', $event)"
        @draft-change="$emit('draftChange', $event)"
        @typing="$emit('typing', $event)"
      />
    </div>
  </aside>
  <span v-else class="hidden" aria-hidden="true" />
</template>
